import React, { useState } from 'react';
import { useQuery } from 'react-query';
import api from '../services/api.ts';
import { DocumentChartBarIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';

// Types
interface ReportData {
  id: string;
  title: string;
  description: string;
  type: 'assets' | 'debts' | 'cashflow' | 'networth';
  period: 'monthly' | 'quarterly' | 'yearly';
  createdAt: string;
}

const Reports: React.FC = () => {
  const [selectedPeriod, setSelectedPeriod] = useState<'monthly' | 'quarterly' | 'yearly'>(
    'monthly'
  );
  const [selectedType, setSelectedType] = useState<
    'all' | 'assets' | 'debts' | 'cashflow' | 'networth'
  >('all');

  const {
    data: reports,
    isLoading,
    error,
  } = useQuery<ReportData[]>(
    ['reports', selectedPeriod, selectedType],
    async () => {
      const response = await api.get('/reports', {
        params: {
          period: selectedPeriod,
          type: selectedType !== 'all' ? selectedType : undefined,
        },
      });
      return response.data;
    },
    {
      staleTime: 1000 * 60 * 5, // 5 minutes
    }
  );

  const filteredReports = reports?.filter(
    report =>
      (selectedType === 'all' || report.type === selectedType) && report.period === selectedPeriod
  );

  const handleGenerateReport = async (type: 'assets' | 'debts' | 'cashflow' | 'networth') => {
    try {
      // In a real app, this would call the API to generate a new report
      alert(`Relatório de ${type} gerado com sucesso!`);
    } catch (error) {
      console.error('Erro ao gerar relatório:', error);
    }
  };

  const handleDownloadReport = async (id: string) => {
    try {
      // In a real app, this would download the report file
      alert(`Download do relatório ${id} iniciado!`);
    } catch (error) {
      console.error('Erro ao baixar relatório:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center text-red-500">
        Erro ao carregar relatórios. Por favor, tente novamente.
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-cyan-600 rounded-2xl p-8 text-white">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">Relatórios</h1>
            <p className="text-primary-100 mt-2">Análises e relatórios do seu patrimônio</p>
          </div>
        </div>
      </div>

      {/* Report Generation Section */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
        <div className="mb-6">
          <h2 className="text-lg leading-6 font-medium text-gray-900">Gerar Novo Relatório</h2>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            Selecione o tipo de relatório que deseja gerar
          </p>
        </div>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          <button
            onClick={() => handleGenerateReport('assets')}
            className="relative block w-full border-2 border-gray-300 border-dashed rounded-xl p-6 text-center hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
          >
            <DocumentChartBarIcon className="mx-auto h-12 w-12 text-gray-400" />
            <span className="mt-2 block text-sm font-medium text-gray-900">
              Relatório de Ativos
            </span>
          </button>
          <button
            onClick={() => handleGenerateReport('debts')}
            className="relative block w-full border-2 border-gray-300 border-dashed rounded-xl p-6 text-center hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
          >
            <DocumentChartBarIcon className="mx-auto h-12 w-12 text-gray-400" />
            <span className="mt-2 block text-sm font-medium text-gray-900">
              Relatório de Dívidas
            </span>
          </button>
          <button
            onClick={() => handleGenerateReport('cashflow')}
            className="relative block w-full border-2 border-gray-300 border-dashed rounded-xl p-6 text-center hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
          >
            <DocumentChartBarIcon className="mx-auto h-12 w-12 text-gray-400" />
            <span className="mt-2 block text-sm font-medium text-gray-900">Fluxo de Caixa</span>
          </button>
          <button
            onClick={() => handleGenerateReport('networth')}
            className="relative block w-full border-2 border-gray-300 border-dashed rounded-xl p-6 text-center hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
          >
            <DocumentChartBarIcon className="mx-auto h-12 w-12 text-gray-400" />
            <span className="mt-2 block text-sm font-medium text-gray-900">Patrimônio Líquido</span>
          </button>
        </div>
      </div>

      {/* Report Filters */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div>
              <label htmlFor="period" className="block text-sm font-medium text-gray-700 mb-2">
                Período
              </label>
              <select
                id="period"
                name="period"
                className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent min-w-[140px]"
                value={selectedPeriod}
                onChange={e =>
                  setSelectedPeriod(e.target.value as 'monthly' | 'quarterly' | 'yearly')
                }
              >
                <option value="monthly">Mensal</option>
                <option value="quarterly">Trimestral</option>
                <option value="yearly">Anual</option>
              </select>
            </div>
            <div>
              <label htmlFor="type" className="block text-sm font-medium text-gray-700 mb-2">
                Tipo
              </label>
              <select
                id="type"
                name="type"
                className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent min-w-[180px]"
                value={selectedType}
                onChange={e =>
                  setSelectedType(
                    e.target.value as 'all' | 'assets' | 'debts' | 'cashflow' | 'networth'
                  )
                }
              >
                <option value="all">Todos</option>
                <option value="assets">Ativos</option>
                <option value="debts">Dívidas</option>
                <option value="cashflow">Fluxo de Caixa</option>
                <option value="networth">Patrimônio Líquido</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Reports List */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg leading-6 font-medium text-gray-900">Relatórios Disponíveis</h2>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            {filteredReports?.length || 0} relatórios encontrados
          </p>
        </div>
        <div>
          <ul className="divide-y divide-gray-200">
            {filteredReports?.length === 0 ? (
              <li className="px-6 py-8">
                <p className="text-sm text-gray-500 text-center">
                  Nenhum relatório encontrado para os filtros selecionados.
                </p>
              </li>
            ) : (
              filteredReports?.map(report => (
                <li key={report.id} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex flex-col">
                      <p className="text-sm font-medium text-primary-600 truncate">
                        {report.title}
                      </p>
                      <p className="mt-1 text-sm text-gray-500">{report.description}</p>
                      <p className="mt-1 text-xs text-gray-400">
                        Gerado em:{' '}
                        {new Date(report.createdAt).toLocaleDateString('pt-BR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </p>
                    </div>
                    <div>
                      <button
                        onClick={() => handleDownloadReport(report.id)}
                        className="inline-flex items-center px-4 py-2 border border-primary-300 text-sm font-medium rounded-xl text-primary-700 bg-white hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
                      >
                        <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                        Download
                      </button>
                    </div>
                  </div>
                </li>
              ))
            )}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Reports;
